import React, { memo } from 'react';
import MUISlider, {
  SliderOwnerState,
  SliderValueLabelProps,
} from '@mui/material/Slider';

import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { Tooltip } from '@app/core';

import { Trans } from 'react-i18next';
import classNames from 'classnames';
import { InputLabel } from './InputLabel';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    ...theme.flexCenter,
    [theme.media.tablet]: {
      padding: [0, theme.marginBase * 2],
    },
  },
  labelContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  disabled: {
    opacity: 0.5,
  },
  slider: {
    '& .MuiSlider-root': {
      color: theme.colors.green,
      height: theme.marginBase,
    },
    '& .MuiSlider-rail': {
      opacity: 1,
      color: theme.colors.greenMuted,
      height: theme.marginBase,
      borderRadius: theme.marginBase / 2,
    },
    '& .MuiSlider-track': {
      height: theme.marginBase,
      borderRadius: theme.marginBase / 2,
    },
    '& .MuiSlider-thumb': {
      height: theme.marginBase * 3,
      width: theme.marginBase * 3,
      boxShadow: theme.boxShadow.base,
      '&:focus, &:hover, &:active': {
        boxShadow: 'inherit',
      },
      '&::after': {
        [theme.media.tablet]: {
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        },
      },
    },
  },
  space: {
    marginBottom: theme.marginBase * 4,
  },
  label: {
    ...theme.fonts.label,
    marginBottom: theme.marginBase / 2,
  },
  error: {
    marginTop: theme.marginBase * 3,
  },
}));

interface LabelProps extends SliderValueLabelProps {
  value: number;
}

const ValueLabelComponent = ({
  children,
  open,
  value,
  ownerState,
}: LabelProps & { ownerState: SliderOwnerState }) => {
  return (
    <Tooltip
      open={open}
      placement="top"
      title={ownerState.max === value ? `${value} +` : value || ''}
      arrow={false}
      small
    >
      {children as any}
    </Tooltip>
  );
};

const ValueLabelComponentNoPlus = ({ children, open, value }: LabelProps) => {
  return (
    <Tooltip open={open} placement="top" title={`${value}`} arrow={false} small>
      {children as any}
    </Tooltip>
  );
};

export const Slider = memo(({ label, noPlus, rightAction, ...rest }: any) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  return (
    <div
      className={classNames(classes.slider, {
        [classes.disabled]: rest.disabled,
      })}
    >
      <InputLabel
        label={label}
        rightAction={rightAction}
        className={!rest.valueLabelDisplay ? classes.space : undefined}
      />
      <div className={classes.container}>
        <MUISlider
          components={{
            ValueLabel: noPlus
              ? ValueLabelComponentNoPlus
              : ValueLabelComponent,
          }}
          valueLabelDisplay="on"
          {...rest}
        />
      </div>
    </div>
  );
});
